import { useState, useEffect, useCallback } from 'react';
import { useFloorPlanScale } from './useFloorPlanScale';
import { toast } from 'sonner';
import { useAppStore } from '@/stores/appStore';

interface UseScaleOnboardingOptions {
  floorPlanId: string;
  onScaleToolClick?: () => void;
  isDwgFile?: boolean;
}

export function useScaleOnboarding({ 
  floorPlanId, 
  onScaleToolClick,
  isDwgFile = false 
}: UseScaleOnboardingOptions) {
  const { hasScale, isLoading } = useFloorPlanScale(floorPlanId);
  const [hasShownTooltip, setHasShownTooltip] = useState(false);
  const [shouldShowTooltip, setShouldShowTooltip] = useState(false);
  
  // Check if we should show the onboarding
  useEffect(() => {
    // Don't show if loading or already has scale
    if (isLoading || hasScale || hasShownTooltip) return;
    
    // Show tooltip after a short delay to avoid overwhelming the user
    const timer = setTimeout(() => {
      setShouldShowTooltip(true);
      
      // Show a toast notification
      toast.info(
        isDwgFile 
          ? "This DWG file may have scale information. Set up scale to measure accurately."
          : "Set up scale to enable accurate measurements on your floor plan.",
        {
          duration: 6000,
          action: {
            label: "Set Scale",
            onClick: () => {
              // Set the settings tab to scale
              const setSettingsTab = useAppStore.getState().setSettingsTab;
              setSettingsTab('scale');
              onScaleToolClick?.();
              dismissTooltip();
            }
          }
        }
      );
      
      setHasShownTooltip(true);
    }, 2000); // 2 second delay
    
    return () => clearTimeout(timer);
  }, [isLoading, hasScale, hasShownTooltip, isDwgFile, onScaleToolClick]);
  
  // Dismiss the tooltip
  const dismissTooltip = useCallback(() => {
    setShouldShowTooltip(false);
  }, []);
  
  // Reset onboarding when floor plan changes
  useEffect(() => {
    setHasShownTooltip(false);
    setShouldShowTooltip(false);
  }, [floorPlanId]);
  
  return {
    shouldShowTooltip,
    dismissTooltip,
    hasScale
  };
}